// All AI / payment / storage calls go through the gateway.
// Env-injected at build time via Vite (VITE_*).
import type { SkinScores, ClaudeRoutine, SkinGoal } from './types';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL as string | undefined;
const APP_TOKEN = import.meta.env.VITE_GATEWAY_APP_TOKEN as string | undefined;

function headers(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-App-Token': APP_TOKEN ?? '',
  };
}

function endpoint(path: string): string {
  if (!GATEWAY_URL) throw new Error('VITE_GATEWAY_URL not configured');
  return `${GATEWAY_URL.replace(/\/$/, '')}${path}`;
}

// ── Skin analysis ───────────────────────────────────────────────────────────
export async function analyzeSkin(jpegBlob: Blob): Promise<SkinScores> {
  const base64 = await blobToBase64(jpegBlob);
  const res = await fetch(endpoint('/skin-analysis'), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ imageBase64: base64, mimeType: 'image/jpeg' }),
  });
  if (!res.ok) {
    const body = await res.text();
    try {
      const parsed = JSON.parse(body);
      throw new Error(parsed.error ?? `Gateway ${res.status}`);
    } catch {
      throw new Error(`Gateway ${res.status}: ${body.slice(0, 120)}`);
    }
  }
  const data = await res.json();
  return data.scores as SkinScores;
}

// ── Routine generation via Claude ───────────────────────────────────────────
export async function generateRoutine(
  scores: SkinScores,
  realAge: number,
  goals: SkinGoal[],
): Promise<ClaudeRoutine> {
  const system = buildRoutineSystem();
  const user = buildRoutineUserPrompt(scores, realAge, goals);

  const res = await fetch(endpoint('/'), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      messages: [{ role: 'user', content: user }],
      system,
      maxTokens: 1800,
    }),
  });
  if (!res.ok) {
    return fallbackRoutine();
  }
  const data = await res.json();
  const text = (data.text ?? '') as string;
  return parseRoutineJSON(text) ?? fallbackRoutine();
}

// ── Plan persistence + Stripe checkout ──────────────────────────────────────
export interface SavedPlanRequest {
  email: string;
  scanResult: unknown; // serialized ScanResult
}

export async function createCheckoutSession(payload: SavedPlanRequest): Promise<{
  url: string;
  planToken: string;
}> {
  const res = await fetch(endpoint('/web/create-checkout-session'), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Checkout init failed (${res.status})`);
  return res.json();
}

export async function fetchPlan(token: string): Promise<{
  status: 'paid' | 'pending' | 'not_found';
  scanResult?: unknown;
  email?: string;
}> {
  const res = await fetch(endpoint(`/web/plan/${encodeURIComponent(token)}`), {
    headers: { 'X-App-Token': APP_TOKEN ?? '' },
  });
  if (res.status === 404) return { status: 'not_found' };
  if (!res.ok) throw new Error(`Plan fetch failed (${res.status})`);
  return res.json();
}

// ── Helpers ─────────────────────────────────────────────────────────────────

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // result is "data:image/jpeg;base64,xxxx" — strip the prefix
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function buildRoutineSystem(): string {
  return [
    "You are Lumé, a clinical skincare advisor. Generate a personalised routine as STRICT JSON only — no prose, no markdown fences.",
    "Output schema:",
    "{",
    '  "summary": "1-2 sentence read of the skin",',
    '  "amSteps": [{ "category": "cleanser|serum|treatment|moisturizer|spf|eye_cream", "ingredient": "<slug like vitamin_c, retinol, niacinamide, hyaluronic_acid, ceramides, peptides, salicylic_acid, azelaic_acid, spf, gentle_cleanser, ceramide_moisturizer>", "concentrationOrForm": "optional, e.g. 15% or SPF 50", "rationale": "1 sentence why" }],',
    '  "pmSteps": [...],',
    '  "warnings": ["..."],',
    '  "expectedResultsTimeline": "1-2 sentences"',
    "}",
    "Constraints: 3–5 AM steps, 3–5 PM steps. Ingredient slugs MUST be lowercase_underscore.",
  ].join('\n');
}

function buildRoutineUserPrompt(
  scores: SkinScores,
  realAge: number,
  goals: SkinGoal[],
): string {
  return [
    `Real age: ${realAge}`,
    `Goals: ${goals.join(', ') || 'general'}`,
    'Scores (0-100, higher = more concern):',
    `  wrinkles ${scores.wrinkles.overall}`,
    `  pigmentation ${scores.pigmentation.overall}`,
    `  redness ${scores.redness.overall}`,
    `  texture ${scores.texture.overall}`,
    `  pores ${scores.pores.overall}`,
    `  dark_circles ${scores.dark_circles.overall}`,
    scores.hydration ? `  hydration ${scores.hydration.overall}` : '',
    scores.acne ? `  acne ${scores.acne.overall}` : '',
    scores.oiliness ? `  oiliness ${scores.oiliness.overall}` : '',
    scores.firmness ? `  firmness ${scores.firmness.overall}` : '',
    '',
    'Return the JSON only.',
  ]
    .filter(Boolean)
    .join('\n');
}

function parseRoutineJSON(text: string): ClaudeRoutine | null {
  // Strip ``` fences if model added them
  const cleaned = text.replace(/```json|```/g, '').trim();
  // Locate first { and last } in case there's prose around it
  const start = cleaned.indexOf('{');
  const end = cleaned.lastIndexOf('}');
  if (start === -1 || end === -1) return null;
  try {
    const obj = JSON.parse(cleaned.slice(start, end + 1));
    return {
      amSteps: obj.amSteps ?? [],
      pmSteps: obj.pmSteps ?? [],
      summary: obj.summary ?? '',
      warnings: obj.warnings ?? [],
      expectedResultsTimeline: obj.expectedResultsTimeline ?? '',
    };
  } catch {
    return null;
  }
}

function fallbackRoutine(): ClaudeRoutine {
  return {
    summary:
      "We couldn't reach the routine engine, so here's a safe foundation you can build on.",
    amSteps: [
      { category: 'cleanser', ingredient: 'gentle_cleanser', rationale: 'Calm wake-up cleanse.' },
      { category: 'serum', ingredient: 'vitamin_c', concentrationOrForm: '10%', rationale: 'Antioxidant defense before sun.' },
      { category: 'moisturizer', ingredient: 'ceramides', rationale: 'Strengthen the barrier.' },
      { category: 'spf', ingredient: 'spf', concentrationOrForm: 'SPF 50', rationale: 'Non-negotiable.' },
    ],
    pmSteps: [
      { category: 'cleanser', ingredient: 'gentle_cleanser', rationale: 'Reset the surface.' },
      { category: 'treatment', ingredient: 'retinol', concentrationOrForm: '0.3%', rationale: 'Drive cell turnover overnight.' },
      { category: 'moisturizer', ingredient: 'ceramides', rationale: 'Lock in repair.' },
    ],
    warnings: [],
    expectedResultsTimeline: 'Most visible change shows up between weeks 4 and 8 with consistent use.',
  };
}
